import { Link } from 'react-router-dom';
import type { caseStudies } from '@/mocks/work';

type Project = (typeof caseStudies)[number];

interface ProjectTileProps {
  project: Project;
  frameClass: string;
  reveal: string;
  className?: string;
  large?: boolean;
  compact?: boolean;
}

export default function ProjectTile({ project, frameClass, reveal, className = '', large = false, compact = false }: ProjectTileProps) {
  const titleSize = large
    ? 'text-[clamp(2.2rem,5vw,5rem)]'
    : compact
      ? 'text-[clamp(1.4rem,2vw,2.1rem)]'
      : 'text-[clamp(1.8rem,3.2vw,3.2rem)]';

  return (
    <article className={className}>
      <Link
        to={`/work/${project.slug}`}
        className="group block cursor-pointer rounded-[18px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-4"
        aria-label={`${project.title}, ${project.category}, ${project.location}`}
      >
        <div className={`relative overflow-hidden rounded-[18px] bg-foreground-950 ${frameClass}`} data-reveal={reveal}>
          <img src={project.heroSrc} alt={project.heroAlt} title={`${project.title} ${project.category}`} className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-foreground-950/75 via-foreground-950/30 to-transparent pointer-events-none" />
          <div className={`absolute inset-x-0 bottom-0 p-5 md:p-7 flex items-end justify-between gap-x-6 gap-y-2 ${compact ? 'flex-wrap md:flex-nowrap' : 'flex-wrap'}`}>
            <div className={`flex items-baseline gap-4 ${compact ? 'min-w-0' : ''}`}>
              <span className="font-label text-[11px] text-primary-200">{project.no}</span>
              <h2 className={`font-extrabold tracking-[-0.05em] leading-[0.9] text-background-50 group-hover:text-primary-200 transition-colors ${titleSize} ${compact ? 'md:whitespace-nowrap md:truncate' : ''}`}>
                {project.title}
              </h2>
            </div>
            <p className="font-label text-[11px] uppercase tracking-[0.16em] text-background-100 whitespace-nowrap shrink-0">
              <span className={compact ? 'md:hidden' : ''}>{project.category} <span aria-hidden>/</span> </span>{project.location}
              <span className="ml-3 inline-flex w-4 h-4 items-center justify-center align-middle transition-transform group-hover:rotate-45"><i className="ri-arrow-right-up-line" /></span>
            </p>
          </div>
        </div>
      </Link>
    </article>
  );
}